import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { useMemo } from 'react'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

type StandardTaskState = StandardTask['state']

// Tasks in these states are excluded from the default fetch and only loaded
// when explicitly opted in via the `extraStates` arg.
const OPT_IN_STATES = [
  StandardTask.stateSchema.enum.done,
  StandardTask.stateSchema.enum.canceled,
  StandardTask.stateSchema.enum.duplicate,
] as const satisfies readonly StandardTaskState[]

interface UseTasksResult {
  data: StandardTask[]
  isLoading: boolean
}

export function useTasks(
  extraStates: StandardTaskState[] = []
): UseTasksResult {
  // We parallelize the requests for faster loading of the most commonly viewed tasks (in progress and todo).

  const { data: inProgressTasks, isLoading: isLoadingInProgressTasks } =
    useSearchResourcePages(
      StandardTask,
      StandardTask.listPageSearchQuery({
        'business-status': [StandardTask.stateSchema.enum['in-progress']],
      })
    )

  const { data: todoTasks, isLoading: isLoadingTodoTasks } =
    useSearchResourcePages(
      StandardTask,
      StandardTask.listPageSearchQuery({
        'business-status': [StandardTask.stateSchema.enum.todo],
      })
    )

  const { data: backlogTasks, isLoading: isLoadingBacklogTasks } =
    useSearchResourcePages(
      StandardTask,
      StandardTask.listPageSearchQuery({
        'business-status': [StandardTask.stateSchema.enum.backlog],
      })
    )

  const optedInStates = useMemo(
    () =>
      extraStates.filter((s): s is (typeof OPT_IN_STATES)[number] =>
        (OPT_IN_STATES as readonly StandardTaskState[]).includes(s)
      ),
    [extraStates]
  )

  const { data: optedInTasks, isLoading: isLoadingOptedInTasks } =
    useSearchResourcePages(
      StandardTask,
      optedInStates.length > 0
        ? StandardTask.listPageSearchQuery({
            'business-status': optedInStates,
          })
        : undefined
    )

  const tasks = useMemo(
    () => [
      ...(inProgressTasks ?? []),
      ...(todoTasks ?? []),
      ...(backlogTasks ?? []),
      ...(optedInTasks ?? []),
    ],
    [inProgressTasks, todoTasks, backlogTasks, optedInTasks]
  )

  const isLoading =
    isLoadingInProgressTasks ||
    isLoadingTodoTasks ||
    isLoadingBacklogTasks ||
    (optedInStates.length > 0 && isLoadingOptedInTasks)

  return { data: tasks, isLoading }
}
